"use client";

import { useState } from "react";
import { FileCheck02, HelpCircle, RefreshCw05, Trash01, XClose } from "@untitledui/icons";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/base/buttons/button";
import { getErrorDisplayMessage } from "@/lib/error-message-parser";
import { openCrispChat } from "@/utils/crisp";
import {
    ActionButtons,
    AttentionList,
    BadgeRow,
    CardContainer,
    CardFooter,
    CardHeaderSection,
    CardProgress,
    FileLink,
    InvoiceHeader,
    IssueDetailsTable,
    StatusBadge,
    StatusMessage,
} from "./components";
import { CancelFileModal, ContactVendorModal, DeleteFileModal, ExportWithIssuesModal } from "./modals";

// =============================================================================
// TYPES
// =============================================================================

export type UploadStatus =
    | "uploading"
    | "queued"
    | "processing"
    | "connecting"
    | "success"
    | "success-with-caveats"
    | "exported"
    | "error"
    | "duplicate"
    | "no-match"
    | "processing-error";

export interface DetailedIssue {
    type: "price-variance" | "unmatched-item" | "quantity-mismatch" | "missing-po";
    severity: "warning" | "error";
    lineNumber?: number;
    lineReference?: string;
    itemNumber?: string;
    description: string;
    impact: string;
    dollarImpact?: string;
    details?: {
        invoiceValue?: string;
        poValue?: string;
        itemDescription?: string;
        quantity?: number;
        /** Unit price for quantity mismatch issues (to show alongside quantity comparison) */
        unitPrice?: string;
    };
}

export interface UploadStatusCardProps {
    filename: string;
    /** Auto-generated document name to display instead of filename (e.g., "Invoice_VendorName_2026-01-15_INV001") */
    generatedName?: string;
    status: UploadStatus;
    /** Progress percentage (0-100) */
    progress?: number;
    /** Status label text to display */
    statusLabel?: string;
    /** Error code from the latest exception (use getErrorDisplayMessage to get user-friendly message) */
    errorCode?: string;
    /** Human-readable error message from the latest exception (dev mode only) */
    errorMessage?: string;
    /** When true, switches processing card to orange warning styling indicating a retry is pending */
    hasRetryWarning?: boolean;
    pageCount?: number;
    fileSize?: number;
    /** Document type name from API */
    documentType?: string;
    /** Document information for display */
    document?: {
        id: number;
        vendor?: string;
        date?: string;
        amount?: string;
        description?: string;
        documentNumber?: string;
    };
    issues?: string[];
    detailedIssues?: DetailedIssue[];
    analysisSummary?: string;
    varianceInfo?: {
        amount: string;
        direction: "over" | "under";
    };
    duplicateInfo?: {
        originalFilename: string;
        uploadedDate: string;
    };
    isExporting?: boolean;
    /** Whether to show the progress bar (used to hide during bridge outage) */
    showProgress?: boolean;
    onCancel?: () => void;
    onExport?: () => void;
    onRemove?: () => void;
    onGetHelp?: () => void;
    onViewFile?: () => void;
    onReprocess?: () => void;
    onContactVendor?: () => void;
    /** Called when "Go to Document" is clicked */
    onGoToDocument?: (documentId: number) => void;
}

// =============================================================================
// HELPERS
// =============================================================================

const formatFileSize = (bytes?: number): string => {
    if (!bytes) return "0 KB";
    const suffixes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.floor(bytes / Math.pow(1024, i)) + " " + suffixes[i];
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function UploadStatusCard({
    filename,
    generatedName,
    status,
    progress = 0,
    statusLabel,
    errorCode,
    errorMessage,
    hasRetryWarning,
    pageCount,
    fileSize,
    documentType,
    document,
    issues,
    detailedIssues,
    analysisSummary,
    varianceInfo,
    duplicateInfo,
    isExporting: _isExporting = false,
    showProgress = true,
    onCancel,
    onExport,
    onRemove,
    onGetHelp: _onGetHelp,
    onViewFile,
    onReprocess: _onReprocess,
    onContactVendor: _onContactVendor,
    onGoToDocument,
}: UploadStatusCardProps) {
    const navigate = useNavigate();

    // Progress value from props (calculated by parent/polling hook)
    const progressValue = progress;

    // Check if we have document data for display
    const hasDocumentData = !!(document?.vendor || document?.documentNumber);

    // Helper to get display title
    // Priority: vendor name > generatedName > filename
    const getCardTitle = () => {
        if (document?.vendor) return document.vendor;
        if (generatedName) return generatedName;
        return filename;
    };

    // Helper to get display name (generatedName preferred over filename)
    const getDisplayName = () => {
        return generatedName || filename;
    };

    // Get label for display (use prop or default)
    const getStatusLabel = () => {
        return statusLabel || "Processing...";
    };

    // =============================================================================
    // STATE MANAGEMENT
    // =============================================================================

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showContactVendorModal, setShowContactVendorModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);

    // =============================================================================
    // HANDLERS
    // =============================================================================

    const handleViewFile = () => {
        if (onViewFile) {
            onViewFile();
        } else {
            window.open("", "_blank");
        }
    };

    const _handleExportClick = async () => {
        if (status === "success-with-caveats" && issues && issues.length > 0) {
            setShowExportModal(true);
        } else {
            onExport?.();
        }
    };

    const handleExportConfirm = async () => {
        onExport?.();
    };

    // =============================================================================
    // SHARED RENDER HELPERS
    // =============================================================================

    const renderModals = () => (
        <>
            {showDeleteModal && (
                <DeleteFileModal isOpen={showDeleteModal} onOpenChange={setShowDeleteModal} filename={getDisplayName()} onConfirm={() => onRemove?.()} />
            )}
            {showExportModal && issues && (
                <ExportWithIssuesModal isOpen={showExportModal} onOpenChange={setShowExportModal} issues={issues} onConfirm={handleExportConfirm} />
            )}
            {showContactVendorModal && document && (
                <ContactVendorModal
                    isOpen={showContactVendorModal}
                    onOpenChange={setShowContactVendorModal}
                    vendorName={document.vendor || "Vendor"}
                    issues={issues || []}
                    invoiceInfo={{
                        invoiceNumber: document.documentNumber,
                        date: document.date,
                        amount: document.amount,
                    }}
                />
            )}
            {showCancelModal && (
                <CancelFileModal
                    isOpen={showCancelModal}
                    onOpenChange={setShowCancelModal}
                    filename={document?.vendor || getDisplayName()}
                    isInvoice={hasDocumentData}
                    onConfirm={() => onCancel?.()}
                />
            )}
        </>
    );

    /** Handle "Go to Document" click */
    const handleGoToDocument = () => {
        if (document?.id) {
            if (onGoToDocument) {
                onGoToDocument(document.id);
            } else {
                // Default navigation to documents page with the document selected
                navigate(`/legacy-documents?id=${document.id}`);
            }
        }
    };

    /** Renders export-related action buttons */
    const renderExportActions = (showContactVendor = false) => (
        <>
            {showContactVendor && (
                <>
                    <Button
                        size="md"
                        color="secondary"
                        iconLeading={RefreshCw05}
                        onClick={() => openCrispChat(`I'd like to request a reprocess for file "${getDisplayName()}". The problem is [enter details here]`)}
                    >
                        Reprocess
                    </Button>
                    <Button size="md" color="secondary" iconLeading={Trash01} onClick={() => setShowDeleteModal(true)}>
                        Remove
                    </Button>
                </>
            )}
            {!showContactVendor && (
                <Button size="md" color="secondary" iconLeading={Trash01} onClick={() => setShowDeleteModal(true)}>
                    Remove
                </Button>
            )}
            {/* Show Go to Document button when we have a document */}
            {document?.id && (
                <Button size="md" color="primary" iconTrailing={FileCheck02} onClick={handleGoToDocument}>
                    Go to {documentType || "Document"}
                </Button>
            )}
        </>
    );

    /** Renders error action buttons (Get Help + Remove) */
    const renderErrorActions = () => (
        <>
            <Button size="md" color="secondary" iconLeading={HelpCircle} onClick={() => openCrispChat(`I'm having an issue with "${getDisplayName()}"`)}>
                Get Help
            </Button>
            <Button size="md" color="secondary-destructive" iconLeading={Trash01} onClick={() => setShowDeleteModal(true)}>
                Remove
            </Button>
        </>
    );

    // =============================================================================
    // UPLOADING STATE
    // =============================================================================

    if (status === "uploading") {
        return (
            <>
                <CardContainer animated={showProgress}>
                    <CardHeaderSection>
                        <BadgeRow>
                            <StatusBadge color="gray-blue">{showProgress ? "Uploading" : "Bridge Inactive"}</StatusBadge>
                        </BadgeRow>
                        <InvoiceHeader title={getDisplayName()} fileMetadata={{ fileSize: formatFileSize(fileSize), pageCount }} />
                        <CardProgress value={0} show={showProgress} />
                    </CardHeaderSection>
                    <CardFooter>
                        <div />
                        <ActionButtons>
                            <Button size="md" color="secondary" iconLeading={XClose} onClick={() => setShowCancelModal(true)}>
                                Cancel
                            </Button>
                        </ActionButtons>
                    </CardFooter>
                </CardContainer>
                {renderModals()}
            </>
        );
    }

    // =============================================================================
    // QUEUED STATE
    // =============================================================================

    if (status === "queued") {
        return (
            <>
                <CardContainer animated={showProgress}>
                    <CardHeaderSection>
                        <BadgeRow>
                            <StatusBadge color="gray-blue">{showProgress ? "Queued" : "Bridge Inactive"}</StatusBadge>
                        </BadgeRow>
                        <InvoiceHeader title={getDisplayName()} fileMetadata={{ fileSize: formatFileSize(fileSize), pageCount }} />
                        <StatusMessage>{getStatusLabel()}</StatusMessage>
                        <CardProgress value={progressValue} show={showProgress} />
                    </CardHeaderSection>
                    <CardFooter>
                        <FileLink filename={getDisplayName()} onClick={onViewFile} />
                        <ActionButtons>
                            <Button size="md" color="secondary" iconLeading={XClose} onClick={() => setShowCancelModal(true)}>
                                Cancel
                            </Button>
                        </ActionButtons>
                    </CardFooter>
                </CardContainer>
                {renderModals()}
            </>
        );
    }

    // =============================================================================
    // PROCESSING STATE
    // =============================================================================

    if (status === "processing") {
        return (
            <>
                <CardContainer animated={showProgress}>
                    <CardHeaderSection>
                        <BadgeRow documentType={documentType}>
                            <StatusBadge color={hasRetryWarning ? "warning" : "gray-blue"}>
                                {hasRetryWarning ? "Retrying" : showProgress ? "Processing" : "Bridge Inactive"}
                            </StatusBadge>
                        </BadgeRow>
                        <InvoiceHeader
                            title={document?.vendor || getCardTitle()}
                            invoiceNumber={document?.documentNumber}
                            subtitle={document?.date}
                            description={document?.description}
                            amount={document?.amount}
                        />
                        {hasRetryWarning ? (
                            <StatusMessage variant="warning">A transient error occurred. This job will be retried soon.</StatusMessage>
                        ) : (
                            <StatusMessage>{getStatusLabel()}</StatusMessage>
                        )}
                        <CardProgress value={progressValue} show={showProgress} />
                    </CardHeaderSection>
                    <CardFooter>
                        <FileLink filename={getDisplayName()} onClick={onViewFile} />
                        <ActionButtons>
                            <Button size="md" color="secondary" iconLeading={XClose} onClick={() => setShowCancelModal(true)}>
                                Cancel
                            </Button>
                        </ActionButtons>
                    </CardFooter>
                </CardContainer>
                {renderModals()}
            </>
        );
    }

    // =============================================================================
    // CONNECTING/MATCHING STATE (kept for backward compatibility)
    // =============================================================================

    if (status === "connecting") {
        return (
            <>
                <CardContainer animated={showProgress}>
                    <CardHeaderSection>
                        <BadgeRow documentType={documentType}>
                            <StatusBadge color="gray-blue">{showProgress ? "Processing" : "Bridge Inactive"}</StatusBadge>
                        </BadgeRow>
                        <InvoiceHeader
                            title={document?.vendor || getCardTitle()}
                            invoiceNumber={document?.documentNumber}
                            subtitle={document?.date}
                            description={document?.description}
                            amount={document?.amount}
                        />
                        <StatusMessage>{getStatusLabel()}</StatusMessage>
                        <CardProgress value={progressValue} show={showProgress} />
                    </CardHeaderSection>
                    <CardFooter>
                        <FileLink filename={getDisplayName()} onClick={onViewFile} />
                        <ActionButtons>
                            <Button size="md" color="secondary" iconLeading={XClose} onClick={() => setShowCancelModal(true)}>
                                Cancel
                            </Button>
                        </ActionButtons>
                    </CardFooter>
                </CardContainer>
                {renderModals()}
            </>
        );
    }

    // =============================================================================
    // SUCCESS STATE
    // =============================================================================

    if (status === "success") {
        return (
            <>
                <CardContainer>
                    <CardHeaderSection>
                        <BadgeRow documentType={documentType}>
                            <StatusBadge color="brand">Complete</StatusBadge>
                        </BadgeRow>
                        <InvoiceHeader
                            title={document?.vendor || getCardTitle()}
                            invoiceNumber={document?.documentNumber}
                            subtitle={document?.date}
                            description={document?.description}
                            amount={document?.amount}
                        />
                        <StatusMessage variant="brand">Ready for export</StatusMessage>
                    </CardHeaderSection>
                    <CardFooter>
                        <FileLink filename={getDisplayName()} onClick={handleViewFile} />
                        <ActionButtons>{renderExportActions()}</ActionButtons>
                    </CardFooter>
                </CardContainer>
                {renderModals()}
            </>
        );
    }

    // =============================================================================
    // SUCCESS WITH CAVEATS STATE
    // =============================================================================

    if (status === "success-with-caveats" && (issues || detailedIssues)) {
        return (
            <>
                <CardContainer>
                    <CardHeaderSection>
                        <BadgeRow documentType={documentType}>
                            <StatusBadge color="warning">Needs Review</StatusBadge>
                        </BadgeRow>
                        <InvoiceHeader
                            title={document?.vendor || getCardTitle()}
                            invoiceNumber={document?.documentNumber}
                            subtitle={document?.date}
                            description={document?.description}
                            amount={document?.amount}
                            varianceInfo={varianceInfo}
                        />

                        {analysisSummary && (
                            <div className="mt-5 rounded-r-lg border-l-4 border-utility-warning-500 bg-utility-warning-50 px-4 py-3">
                                <div className="mb-1.5 text-xs font-semibold tracking-wider text-utility-warning-700 uppercase">Analysis Summary</div>
                                <p className="text-sm leading-relaxed text-secondary">{analysisSummary}</p>
                            </div>
                        )}

                        {detailedIssues && detailedIssues.length > 0 ? (
                            <IssueDetailsTable issues={detailedIssues} />
                        ) : (
                            issues && <AttentionList items={issues} />
                        )}
                    </CardHeaderSection>
                    <CardFooter>
                        <FileLink filename={getDisplayName()} onClick={handleViewFile} />
                        <ActionButtons>{renderExportActions(true)}</ActionButtons>
                    </CardFooter>
                </CardContainer>
                {renderModals()}
            </>
        );
    }

    // =============================================================================
    // EXPORTED STATE
    // =============================================================================

    if (status === "exported") {
        return (
            <>
                <CardContainer>
                    <CardHeaderSection>
                        <BadgeRow documentType={documentType}>
                            <StatusBadge color="success">Exported</StatusBadge>
                        </BadgeRow>
                        <InvoiceHeader
                            title={document?.vendor || getCardTitle()}
                            invoiceNumber={document?.documentNumber}
                            subtitle={document?.date}
                            description={document?.description}
                            amount={document?.amount}
                        />
                        <StatusMessage variant="success">Successfully exported</StatusMessage>
                    </CardHeaderSection>
                    <CardFooter>
                        <FileLink filename={getDisplayName()} onClick={handleViewFile} />
                        <ActionButtons>
                            <Button size="md" color="secondary" iconLeading={Trash01} onClick={() => setShowDeleteModal(true)}>
                                Clear
                            </Button>
                        </ActionButtons>
                    </CardFooter>
                </CardContainer>
                {renderModals()}
            </>
        );
    }

    // =============================================================================
    // PROCESSING ERROR STATE
    // =============================================================================

    if (status === "processing-error") {
        return (
            <>
                <CardContainer>
                    <CardHeaderSection>
                        <BadgeRow documentType={documentType}>
                            <StatusBadge color="error">Error Occurred</StatusBadge>
                        </BadgeRow>
                        {hasDocumentData ? (
                            <InvoiceHeader
                                title={document?.vendor || getCardTitle()}
                                invoiceNumber={document?.documentNumber}
                                subtitle={document?.date}
                                description={document?.description}
                                amount={document?.amount}
                            />
                        ) : (
                            <InvoiceHeader title={getDisplayName()} fileMetadata={{ fileSize: formatFileSize(fileSize), pageCount }} />
                        )}
                        <StatusMessage variant="error">{errorCode ? getErrorDisplayMessage(errorCode) : "An error occurred during processing"}</StatusMessage>
                    </CardHeaderSection>
                    <CardFooter>
                        <FileLink filename={getDisplayName()} onClick={handleViewFile} />
                        <ActionButtons>{renderErrorActions()}</ActionButtons>
                    </CardFooter>
                </CardContainer>
                {renderModals()}
            </>
        );
    }

    // =============================================================================
    // DUPLICATE STATE
    // =============================================================================

    if (status === "duplicate") {
        return (
            <>
                <CardContainer>
                    <CardHeaderSection>
                        <BadgeRow documentType={documentType}>
                            <StatusBadge color="error">Duplicate</StatusBadge>
                        </BadgeRow>
                        <InvoiceHeader
                            title={document?.vendor || getCardTitle()}
                            invoiceNumber={document?.documentNumber}
                            subtitle={document?.date}
                            description={document?.description}
                            amount={document?.amount}
                        />
                        <StatusMessage variant="error">
                            {duplicateInfo
                                ? `Already uploaded as "${duplicateInfo.originalFilename}" on ${duplicateInfo.uploadedDate}`
                                : "This file appears to be a duplicate"}
                        </StatusMessage>
                    </CardHeaderSection>
                    <CardFooter>
                        <FileLink filename={getDisplayName()} onClick={handleViewFile} />
                        <ActionButtons>{renderErrorActions()}</ActionButtons>
                    </CardFooter>
                </CardContainer>
                {renderModals()}
            </>
        );
    }

    // =============================================================================
    // NO MATCH STATE
    // =============================================================================

    if (status === "no-match") {
        return (
            <>
                <CardContainer>
                    <CardHeaderSection>
                        <BadgeRow documentType={documentType}>
                            <StatusBadge color="error">No Match</StatusBadge>
                        </BadgeRow>
                        <InvoiceHeader
                            title={document?.vendor || getCardTitle()}
                            invoiceNumber={document?.documentNumber}
                            subtitle={document?.date}
                            description={document?.description}
                            amount={document?.amount}
                        />
                        <StatusMessage variant="error">Could not find a matching record</StatusMessage>
                    </CardHeaderSection>
                    <CardFooter>
                        <FileLink filename={getDisplayName()} onClick={handleViewFile} />
                        <ActionButtons>{renderErrorActions()}</ActionButtons>
                    </CardFooter>
                </CardContainer>
                {renderModals()}
            </>
        );
    }

    // =============================================================================
    // GENERIC ERROR STATE
    // =============================================================================

    if (status === "error") {
        // Use error code to get display message
        let displayMessage: string;

        if (errorCode) {
            displayMessage = getErrorDisplayMessage(errorCode);
        } else if (errorMessage) {
            displayMessage = errorMessage;
        } else {
            displayMessage = "An error occurred";
        }

        const badgeText = "Error Occurred";

        // Check if this is a duplicate file error
        const isDuplicate = errorCode === "BizDuplicateFile";

        return (
            <>
                <CardContainer>
                    <CardHeaderSection>
                        <BadgeRow documentType={documentType}>
                            <StatusBadge color="error">{isDuplicate ? "Duplicate" : badgeText}</StatusBadge>
                        </BadgeRow>
                        {hasDocumentData ? (
                            <InvoiceHeader
                                title={document?.vendor || getCardTitle()}
                                invoiceNumber={document?.documentNumber}
                                subtitle={document?.date}
                                description={document?.description}
                                amount={document?.amount}
                            />
                        ) : (
                            <InvoiceHeader title={getDisplayName()} fileMetadata={{ fileSize: formatFileSize(fileSize), pageCount }} />
                        )}
                        <StatusMessage variant="error">{displayMessage}</StatusMessage>
                    </CardHeaderSection>
                    <CardFooter>
                        <div></div>
                        <ActionButtons>{renderErrorActions()}</ActionButtons>
                    </CardFooter>
                </CardContainer>
                {renderModals()}
            </>
        );
    }

    return null;
}
